import { useMemo, useState } from "react";
import { CATEGORIES, hotList, type HotItem } from "@/mocks/melon";

function rankStyle(i: number) {
  if (i === 0) return "bg-primary-500 text-background-50";
  if (i === 1) return "bg-primary-600 text-background-50";
  if (i === 2) return "bg-accent-500 text-foreground-950";
  return "bg-background-200 text-foreground-600";
}

function HotList() {
  const [active, setActive] = useState<string>("全部");
  const [items, setItems] = useState<HotItem[]>(hotList);
  const [refreshing, setRefreshing] = useState(false);

  const shown = useMemo(() => {
    const base = active === "全部" ? items : items.filter((i) => i.cat === active);
    return base.map((it, idx) => ({ ...it, display: idx + 1 }));
  }, [active, items]);

  const refresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    const shuffled = [...hotList].sort(() => Math.random() - 0.5);
    window.setTimeout(() => {
      setItems(shuffled);
      setRefreshing(false);
    }, 450);
  };

  return (
    <section id="hot" className="scroll-mt-24 bg-background-100/60">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary-600">Live Ranking</p>
            <h2 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-4xl">
              全网实时热搜榜
            </h2>
            <p className="mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">
              跟随话题热度秒级滚动更新，正在发酵的大瓜小瓜都在这。点击标签可只看某类热点。
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-700">
              <span className="h-2 w-2 rounded-full bg-primary-500 animate-pulse" />
              {shown.length} 条在榜
            </span>
            <button
              type="button"
              onClick={refresh}
              className="inline-flex h-10 items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-4 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer whitespace-nowrap"
            >
              <i className={`text-[15px] ${refreshing ? "ri-loader-4-line animate-spin" : "ri-refresh-line"}`} />
              {refreshing ? "更新中" : "刷新榜单"}
            </button>
          </div>
        </div>

        <div className="mt-7 flex flex-wrap items-center gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setActive(c)}
              className={`inline-flex h-9 items-center rounded-full px-4 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                active === c
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50 text-foreground-600 hover:bg-background-200 hover:text-foreground-800"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-background-200 bg-background-50">
          {shown.map((it, i) => (
            <div
              key={`${it.id}-${i}`}
              className={`group flex items-center gap-3 border-background-200 px-4 py-3.5 transition-colors hover:bg-background-100 md:gap-5 md:px-6 ${
                i !== shown.length - 1 ? "border-b" : ""
              }`}
            >
              <span
                className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-extrabold ${
                  rankStyle(i)
                }`}
              >
                {it.display}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <h3 className="truncate text-sm font-bold text-foreground-950 group-hover:text-primary-700 md:text-[15px]">
                    <a href="#latest">{it.title}</a>
                  </h3>
                  <span className="rounded bg-background-100 px-1.5 py-0.5 text-[11px] font-medium text-foreground-500">
                    {it.cat}
                  </span>
                </div>
                <p className="mt-0.5 hidden truncate text-xs text-foreground-500 sm:block">{it.brief}</p>
              </div>
              <span className="hidden shrink-0 items-center gap-1 text-xs text-foreground-400 md:flex">
                <i className="ri-fire-line text-primary-500" />
                热 {it.heat}
              </span>
              <i
                className={`hidden shrink-0 text-base md:block ${
                  it.trend === "up"
                    ? "ri-arrow-up-line text-primary-600"
                    : it.trend === "down"
                      ? "ri-arrow-down-line text-secondary-500"
                      : "ri-arrow-right-line text-foreground-300"
                }`}
              />
              <a
                href="#latest"
                aria-label={`查看「${it.title}」`}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-foreground-400 opacity-0 transition-all group-hover:opacity-100 hover:bg-background-200 hover:text-foreground-800 cursor-pointer"
              >
                <i className="ri-arrow-right-up-line" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default HotList;